import LiveKitWaitingRoom from './LiveKitWaitingRoom';
import { handOffPreviewTracks } from './preview-tracks';
import usePreviewDevices from './usePreviewDevices';
import usePreviewMedia from './usePreviewMedia';

import beepAudio from '../../assets/sounds/beep.mp3';
import TelemedicineContext from '../../context/telemedicine';
import useDeviceList from '../../hooks/useDeviceList';
import { clearPreferredAudioOutputId, getPreferredAudioOutputId, playAudioOutputTest, savePreferredAudioOutputId } from '../../utils/livekit-audio-output';
import { discardPreparedRoom, prepareRoom } from '../../utils/livekit-room';

import { showErrorToast } from '@amigoapp/doca-react';
import { usePersistentUserChoices } from '@livekit/components-react';
import { supportsAudioOutputSelection } from 'livekit-client';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSound from 'use-sound';

const LiveKitWaitingRoomContainer = () => {
	const navigate = useNavigate();
	const [play] = useSound(beepAudio, { volume: 1 });
	const { telemedicine, updateTelemedicine } = useContext(TelemedicineContext);

	const {
		userChoices,
		saveAudioInputEnabled,
		saveVideoInputEnabled,
		saveAudioInputDeviceId,
		saveVideoInputDeviceId
	} = usePersistentUserChoices();

	const [isDoctorReady, setIsDoctorReady] = useState(false);
	const [audioEnabled, setAudioEnabled] = useState(userChoices.audioEnabled);
	const [videoEnabled, setVideoEnabled] = useState(userChoices.videoEnabled);
	const [showDeviceSettings, setShowDeviceSettings] = useState(false);
	const [activeSpeakerId, setActiveSpeakerId] = useState(getPreferredAudioOutputId());
	const [activeMicId, setActiveMicId] = useState(userChoices.audioDeviceId || '');

	const showSpeakerSection = useMemo(() => supportsAudioOutputSelection(), []);

	const joiningRef = useRef(false);

	const { url, token } = telemedicine.livekit || {};

	const preparedRoom = useMemo(() => (url && token
		? prepareRoom({
			url,
			token,
			options: {
				videoDeviceId: userChoices.videoDeviceId,
				audioDeviceId: userChoices.audioDeviceId
			}
		})
		: null
	// eslint-disable-next-line react-hooks/exhaustive-deps
	), [url, token]);

	useEffect(() => () => {
		if (!joiningRef.current) {
			discardPreparedRoom();
		}
	}, []);

	const { videoTrack, handedOffRef, status: previewStatus } = usePreviewMedia({
		videoDeviceId: userChoices.videoDeviceId
	});

	const {
		devices: cameraDevices,
		activeDeviceId: activeCameraId,
		isSwitching: isSwitchingCamera,
		selectDevice: selectCamera
	} = usePreviewDevices('videoinput', videoTrack, 'Não foi possível trocar a câmera.');

	const micDevices = useDeviceList('audioinput', showDeviceSettings);
	const speakerDevices = useDeviceList('audiooutput', showDeviceSettings);

	useEffect(() => {
		if (speakerDevices.length === 0) {
			return;
		}

		setActiveSpeakerId(current => {
			if (!current || speakerDevices.some(device => device.deviceId === current)) {
				return current;
			}

			clearPreferredAudioOutputId();

			return '';
		});
	}, [speakerDevices]);

	const onToggleAudio = useCallback(() => {
		setAudioEnabled(current => {
			saveAudioInputEnabled(!current);

			return !current;
		});
	}, [saveAudioInputEnabled]);

	const onToggleVideo = useCallback(() => {
		setVideoEnabled(current => {
			saveVideoInputEnabled(!current);

			return !current;
		});
	}, [saveVideoInputEnabled]);

	const onToggleDeviceSettings = useCallback(() => {
		setShowDeviceSettings(current => !current);
	}, []);

	const rememberInRoom = useCallback((kind, deviceId) => {
		preparedRoom?.switchActiveDevice(kind, deviceId).catch(() => {});
	}, [preparedRoom]);

	const onSelectCamera = useCallback(deviceId => {
		saveVideoInputDeviceId(deviceId);
		rememberInRoom('videoinput', deviceId);
		selectCamera(deviceId);
	}, [selectCamera, saveVideoInputDeviceId, rememberInRoom]);

	const onSelectMic = useCallback(deviceId => {
		setActiveMicId(deviceId);
		saveAudioInputDeviceId(deviceId);
		rememberInRoom('audioinput', deviceId);
	}, [saveAudioInputDeviceId, rememberInRoom]);

	const onSelectSpeaker = useCallback(deviceId => {
		setActiveSpeakerId(deviceId);
		savePreferredAudioOutputId(deviceId);
	}, []);

	const onTestSpeaker = useCallback(async () => {
		try {
			await playAudioOutputTest(beepAudio, activeSpeakerId);
		} catch (error) {
			clearPreferredAudioOutputId();
			setActiveSpeakerId('');
			showErrorToast('Não foi possível reproduzir na saída selecionada.');
		}
	}, [activeSpeakerId]);

	const onJoinCall = useCallback(() => {
		joiningRef.current = true;

		if (videoTrack && !videoEnabled) {
			videoTrack.stop();
		}

		handedOffRef.current = true;

		handOffPreviewTracks([videoEnabled ? videoTrack : null]);

		navigate('/video-room');
	}, [videoTrack, videoEnabled, handedOffRef, navigate]);

	useEffect(() => {
		const handleReady = async () => {
			await play();

			setIsDoctorReady(true);
		};

		window.socket.on('reconnect', () => {
			window.socket.emit('telemedicine:enter-room', {
				room_id: telemedicine?.id,
				patient_id: telemedicine?.patient_id
			});
		});

		window.socket.on('telemedicine:start-video-call', room => {
			updateTelemedicine({
				room_sid: room.room_sid,
				room_token: room.token
			});

			handleReady();
		});

		window.socket.on('telemedicine:connected', handleReady);

		window.socket.emit('telemedicine:enter-room', {
			room_id: telemedicine.id,
			patient_id: telemedicine.patient_id
		});

		return () => {
			window.socket.off('telemedicine:connected', handleReady);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<LiveKitWaitingRoom
			activeCameraId={activeCameraId}
			activeMicId={activeMicId}
			activeSpeakerId={activeSpeakerId}
			attendance={telemedicine.attendance}
			audioEnabled={audioEnabled}
			cameraDevices={cameraDevices}
			company={telemedicine.company}
			createdAt={telemedicine.created_at}
			isDoctorReady={isDoctorReady}
			isSwitchingCamera={isSwitchingCamera}
			micDevices={micDevices}
			previewStatus={previewStatus}
			showDeviceSettings={showDeviceSettings}
			showSpeakerSection={showSpeakerSection}
			speakerDevices={speakerDevices}
			user={telemedicine.user}
			videoEnabled={videoEnabled}
			videoTrack={videoTrack}
			onJoinCall={onJoinCall}
			onSelectCamera={onSelectCamera}
			onSelectMic={onSelectMic}
			onSelectSpeaker={onSelectSpeaker}
			onTestSpeaker={onTestSpeaker}
			onToggleAudio={onToggleAudio}
			onToggleDeviceSettings={onToggleDeviceSettings}
			onToggleVideo={onToggleVideo}
		/>
	);
};

export default LiveKitWaitingRoomContainer;
